const RELOAD_TIMESTAMP_KEY = "intentico:chunkReloadAt";

// If we already triggered a recovery reload within this window, assume the failure
// is persistent (not just a stale deploy) and stop reloading so the user isn't
// trapped in an infinite refresh loop.
const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * Detects errors caused by a missing/stale code-split chunk. These happen when a
 * new version is deployed while a user still has an older build loaded: the hashed
 * chunk the running app asks for no longer exists on the server, so the dynamic
 * import rejects.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : String((error as { message?: unknown })?.message ?? "");

  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /chunkloaderror/i.test(message) ||
    /loading chunk [\d]+ failed/i.test(message) ||
    /is not a valid javascript mime type/i.test(message)
  );
}

/**
 * Forces a single full-page reload to pick up the freshly deployed index.html and
 * its new chunk manifest. Guarded by a recent-reload timestamp so a genuinely
 * persistent failure can't trap the user in a reload loop.
 *
 * @returns true if a reload was triggered, false if suppressed by the guard.
 */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) || 0);
    if (last && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (e.g. private mode) — fall through and reload once.
  }

  window.location.reload();
  return true;
}

// Background/idle chunk preloads can fail after a deploy just like on-demand
// navigations, but a failed *background* preload shouldn't yank the user into a
// reload while they're reading the current page. We suppress recovery only while
// such preloads are in flight; real navigation failures still surface through the
// route's lazy import (and the ErrorBoundary), so they remain covered.
let preloadErrorSuppressionDepth = 0;

export function isPreloadErrorSuppressed(): boolean {
  return preloadErrorSuppressionDepth > 0;
}

/**
 * Runs background chunk preloads with recovery reloads suppressed. Failures are
 * swallowed (the chunk simply isn't warmed); the user only recovers when they
 * actually navigate to the route.
 */
export function suppressPreloadErrors(run: () => Promise<unknown>): Promise<void> {
  preloadErrorSuppressionDepth += 1;
  return Promise.resolve(run())
    .catch(() => {})
    .finally(() => {
      preloadErrorSuppressionDepth -= 1;
    });
}
